import { gql, useQuery } from '@apollo/client';
import ReservesByCountryChartThumbnail from '@assets/images/report-components/reserves-by-country-stacked-by-type-thumbnail.png';
import { useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { getLayout } from '@/admins/SmartReports/components/Components/Charts/helpers';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '@/admins/SmartReports/components/Components/types';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import Graph from '@/components/Graphs/graph';
import {
  AssetAggregateByEnum,
  type AssetAggregates,
  AssetAggregatesFieldsEnum,
} from '@/types/GraphqlSchema';
import { getNestedValue } from '@/utils/helpers';

const QUERY = gql`
  query assetAggregates($input: AssetAggregatesInput!) {
    assetAggregates(input: $input) {
      type
      group
      pdpLiquidReservesSum
      udLiquidReservesSum
      pdpGasReservesMmboeSum
      udGasReservesMmboeSum
    }
  }
`;

export const COMPONENT_ID = 'reserves_by_country_stacked_by_type';

enum ReserveType {
  LIQUIDS_PDP = 'Liquids PDP',
  GAS_PDP = 'Gas PDP',
  LIQUIDS_UD = 'Liquids UD',
  GAS_UD = 'Gas UD',
}

const COLOR_MAP = {
  [ReserveType.LIQUIDS_PDP]: 'palette.chartColors.limeGreen.c100',
  [ReserveType.GAS_PDP]: 'palette.chartColors.trueRed.c75',
  [ReserveType.LIQUIDS_UD]: 'palette.chartColors.limeGreen.c50',
  [ReserveType.GAS_UD]: 'palette.chartColors.trueRed.c50',
};

const useData = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: UseDataType<Array<AssetAggregates>>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const { loading, error } = useQuery<{ assetAggregates: Array<AssetAggregates> }>(QUERY, {
    variables: {
      input: {
        by: AssetAggregateByEnum.Country,
        fields: [
          AssetAggregatesFieldsEnum.UdGasReservesMmboeSum,
          AssetAggregatesFieldsEnum.PdpGasReservesMmboeSum,
          AssetAggregatesFieldsEnum.UdLiquidReservesSum,
          AssetAggregatesFieldsEnum.PdpLiquidReservesSum,
          AssetAggregatesFieldsEnum.TotalReservesSum,
        ],
        portfolio,
        orderBy: AssetAggregatesFieldsEnum.TotalReservesSum,
        order: 'desc',
        limit: 15,
      },
    },
    skip: !!persistedData,
    onCompleted: response => {
      if (response?.assetAggregates) {
        const chartData = response.assetAggregates;
        setChartDataFetched(chartData);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: chartData,
          });
        }
      }
    },
  });

  return { chartData: chartDataFetched, error, loading };
};

const ComponentView = ({ data }: ComponentViewProps<Array<AssetAggregates>>) => {
  const theme = useTheme();

  const formattedData = useMemo(() => {
    try {
      if (!data || !Array.isArray(data)) return null;

      const chartData = data;

      // Define the reserve types we want to stack
      const reserveTypes = [
        ReserveType.GAS_UD,
        ReserveType.LIQUIDS_UD,
        ReserveType.GAS_PDP,
        ReserveType.LIQUIDS_PDP,
      ];

      const formattedChartData = reserveTypes.map((reserveType: ReserveType) => {
        const x = chartData.map((item: AssetAggregates) => item.group); // Countries
        const y = chartData.map((item: AssetAggregates) => {
          let value = 0;

          switch (reserveType) {
            case ReserveType.LIQUIDS_PDP:
              value = item.pdpLiquidReservesSum || 0;
              break;
            case ReserveType.GAS_PDP:
              value = item.pdpGasReservesMmboeSum || 0;
              break;
            case ReserveType.LIQUIDS_UD:
              value = item.udLiquidReservesSum || 0;
              break;
            case ReserveType.GAS_UD:
              value = item.udGasReservesMmboeSum || 0;
              break;
            default:
              value = 0;
          }

          return value;
        });

        const color = getNestedValue(theme, COLOR_MAP[reserveType]);

        return {
          x,
          y,
          type: 'bar',
          name: reserveType,
          marker: { color },
          hovertemplate:
            '%{fullData.name}<br>Country: %{x}<br>Reserves: %{y:,.2f} MMboe<extra></extra>',
        };
      });

      return formattedChartData;
    } catch {
      return null;
    }
  }, [data, theme]);

  if (!formattedData) return null;

  return (
    <ComponentBox>
      <ComponentTitle>Reserves by Country (Stacked by Reserve Type)</ComponentTitle>
      <Graph
        data={formattedData}
        layout={getLayout({ yaxis: { title: { text: 'million boe' } } })}
        title={undefined}
        config={undefined}
        xlabel={undefined}
        ylabel={undefined}
        loading={undefined}
        onUpdate={undefined}
        onClick={undefined}
        onRelayout={undefined}
        onInitialized={undefined}
        onAfterPlot={undefined}
        maxY={undefined}
        darkTheme={undefined}
        context={undefined}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<Array<AssetAggregates>>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>
      Reserves by Country
    </ComponentTitle>
    <img
      src={ReservesByCountryChartThumbnail}
      alt='Reserves By Country Chart Thumbnail'
      style={{ width: '100%', height: 'auto' }}
    />
  </ComponentBox>
);

export const ReservesByCountryStackedByType: ComponentDefinition<Array<AssetAggregates>> = {
  id: COMPONENT_ID,
  name: 'Reserves by Country',
  type: SmartReportComponentTypesEnum.Chart,
  reportTypes: [SmartReportTypeEnum.Corporate],
  Thumbnail: ComponentThumbnail,
  Component: ComponentEdit,
  ComponentView,
};
